/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import getYMM, { getYMMAll } from "pms/product-finder";
import Image from "components/image";
import Loading from "components/loading";
import navigate from "@helpers/link-navigate";
import { ProductFinderContext } from "context/product-finder";
import useEffectWithoutFirstRender from "hooks/useEffectWithoutFirstRender";
import { useLocation } from "@reach/router";
import NProgress from "nprogress";
import sendLog from "@root/utils/new-relic-logger";
import NEW_RELIC_LEVELS from "@root/constants/NEW_RELIC_LEVELS";
import Stars from "components/stars";
import { DesktopDropdowns, MobileDropdowns, defaultBackGroundImages } from "./data";


/**
 * Car / Truck / SUV / Van - car
 *
 * Boats - boat
 *
 * Jet Skis - jet_ski
 *
 * Bimini Tops - bimini
 *
 * Pontoon - pontoon
 *
 * Motorcycle - motorcycle
 *
 * Scooter - scooter
 *
 * ATV - atv
 *
 * UTV - utv
 *
 * Snowmobile - snowmobile
 *
 * RV - rv
 */

const releasedCategories = ['scooter', 'motorcycle', 'atv', 'utv', 'snowmobile', 'car', 'suv', 'truck', 'van', 'boat', 'jet-ski', 'bimini', 'pontoon', 'rv'];

const typeList = [
    { type: "Car / Truck / SUV / Van", value: "car" },
    { type: "RV", value: "rv" },
    { type: "Boat", value: "boat" },
    { type: "Jet Skis", value: "jet_ski" },
    { type: "Bimini Tops", value: "bimini" },
    { type: "Pontoon", value: "pontoon" },
    { type: "Motorcycle", value: "motorcycle" },
    { type: "Scooter", value: "scooter" },
    { type: "ATV", value: "atv" },
    { type: "UTV", value: "utv" },
    { type: "Snowmobile", value: "snowmobile" },
];

const COVER_TYPE_LABELS = {
    car: "Car",
    suv: "SUV",
    truck: "Truck",
    van: "Van",
    boat: "Boat",
    jet_ski: "Jet Ski",
    "jet-ski": "Jet Ski",
    bimini: "Bimini",
    "bimini-tops": "Bimini",
    pontoon: "Pontoon",
    motorcycle: "Motorcycle",
    scooter: "Scooter",
    atv: "ATV",
    utv: "UTV",
    snowmobile: "Snowmobile",
    rv: "RV",
};

const getStickyButtonLabel = (collectionType, subtype) => {
    const typeKey = (subtype && COVER_TYPE_LABELS[subtype] ? subtype : collectionType) || "";
    const typeLabel = COVER_TYPE_LABELS[typeKey];
    return typeLabel ? `Find My ${typeLabel} Cover` : "Find My Cover";
};

const isVisibleChatLauncher = (el) => {
    if (!el) return false;
    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden" || Number(style.opacity) === 0) return false;
    const rect = el.getBoundingClientRect();
    if (rect.width < 24 || rect.height < 24) return false;
    if (rect.height > 180 || rect.width > window.innerWidth * 0.7) return false;
    const nearRight = rect.right >= window.innerWidth - 32;
    const nearBottom = rect.bottom >= window.innerHeight - 120;
    return nearRight && nearBottom;
};

const getVisibleChatLauncher = () => {
    const candidates = [
        document.getElementById("chat-widget-minimized"),
        document.querySelector("#chat-widget-minimized iframe"),
        document.querySelector("#chat-widget-container iframe"),
        document.getElementById("chat-widget-container"),
    ];
    return candidates.find(isVisibleChatLauncher) || null;
};

const getStickyChatOffset = () => {
    const launcher = getVisibleChatLauncher();
    if (!launcher) return 0;
    const gap = 8;
    return Math.max(0, Math.ceil(window.innerWidth - launcher.getBoundingClientRect().left + gap));
};

// Starting state for every product finder (cars, boats, motorcycles, etc)
const initialState = {
    new: {
        make: {
            list: [], selected: null, name: "make",
        },
        model: {
            list: [], selected: null, name: "model",
        },
        year: {
            list: [], selected: null, name: "year",
        },
        option: {
            list: [], selected: null, name: "option",
        },
    },
    withType: {
        type: {
            list: [...typeList], selected: null, name: "type",
        },
        make: {
            list: [], selected: null, name: "make",
        },
        model: {
            list: [], selected: null, name: "model",
        },
        year: {
            list: [], selected: null, name: "year",
        },
        option: {
            list: [], selected: null, name: "option",
        },
    },
    old: {
        make: {
            list: [], selected: null, name: "make",
        },
        year: {
            list: [], selected: null, name: "year",
        },
        model: {
            list: [], selected: null, name: "model",
        },
        option: {
            list: [], selected: null, name: "option",
        },
    },
};

const getSanityColorHex = (color) => (typeof color === "object" && color?.hex ? color.hex : null);

const BackgroundImage = ({ bgImage, pathname, backgroundColor, isShoppingFeed }) => {
    const sanityBgColor = getSanityColorHex(backgroundColor);

    // Sanity color takes precedence over any background image
    if (sanityBgColor) return <div className="element-as-bg rounded-[15px]" style={{ background: sanityBgColor }} />;

    const { asset } = bgImage || {};
    if (bgImage) return <Image className="element-as-bg" width={1240} image={asset} />;

    const defaultBackGroundImageExists = defaultBackGroundImages.find((backgroundImage) => pathname.includes(backgroundImage.key));

    if (defaultBackGroundImageExists && !isShoppingFeed) return <img className="element-as-bg" src={defaultBackGroundImageExists.image} alt={`${defaultBackGroundImageExists.key} - Covers`} />;

    const stringBgColor = typeof backgroundColor === "string" ? backgroundColor : null;
    return <div className="element-as-bg" style={{ background: stringBgColor || "#0E407D" }} />;
};

const Container = ({ bgImage, pathname, children, backgroundColor, isShoppingFeed, containerRef }) => {
    const hasSanityBgColor = !!getSanityColorHex(backgroundColor);

    return (
        <div id="finder" ref={containerRef} className={`product-finder-container ${isShoppingFeed ? "shopping-feed-desktop-container max-w-none" : "container max-w-3xl"}`}>
            <div className={`relative product-finder-container ${hasSanityBgColor ? "rounded-[15px]" : ""}`}>
                <BackgroundImage bgImage={bgImage} pathname={pathname} backgroundColor={backgroundColor} isShoppingFeed={isShoppingFeed} />
                <div className="product-finder shopping-feed-desktop-container-items flex justify-center md:py-16 py-11 md:px-8 px-10 flex-col relative">
                    {children}
                </div>
            </div>
        </div>
    );
};

const Error = ({ bgImage, pathname, backgroundColor }) => (
    <Container bgImage={bgImage} pathname={pathname} backgroundColor={backgroundColor}>
        <h2 className="text-white text-center mb-10 uppercase font-semibold">
            Request could not be processed, please try again ...
        </h2>
    </Container>
);

const encodeFinderParam = (value) => encodeURIComponent(String(value));

const finderQueryParam = (name, value) => `&${name}=${encodeFinderParam(value)}`;

const buildFinderCollectionUrl = (collection) => {
    if (releasedCategories.find((category) => collection?.category_slug?.includes(category))) {
        const urlSegments = [collection.make, collection.model, collection.year, collection.option_slug]
            .filter(Boolean)
            .map(encodeFinderParam);
        const category = collection?.category_slug
            ? collection.category_slug
            : (typeof window !== "undefined" ? window.location.pathname.split("/")[1] : "car-covers");
        return `/${category}/${urlSegments.join("/")}`;
    }

    return `/collections/${encodeFinderParam(collection.handle)}/${encodeFinderParam(collection.year)}`;
};

const addWccParamIfPresent = (url) => {
    if (!url || url === "#") return url;

    const isWccPathActive =
        typeof window !== "undefined" &&
        (window.location.pathname.includes("wcc")
            || window.location.pathname.includes("west-coast")
            || window.location.search.includes("wcc"));
    if (!isWccPathActive) return url;

    if (url.includes("wcc") || url.includes("west-coast")) return url;

    if (typeof window === "undefined") {
        const joiner = url.includes("?") ? "&" : "?";
        return `${url}${joiner}wcc=true`;
    }

    const nextUrl = new URL(url, window.location.origin);
    nextUrl.searchParams.set("wcc", "true");
    return `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
};


const FinderTitle = ({ children, titleColor, as: Tag = "h2" }) => (
    <Tag
        className="text-white text-[24px] md:text-[32px] lg:text-[40px] text-center mb-10 uppercase font-semibold hydrated-show"
        style={titleColor?.hex ? { color: titleColor.hex } : undefined}
    >
        {children}
    </Tag>
);

const ProductFinder = ({ showType = false, newFinder = true, onTypeChange, onChange, data: { title, subtitle, initialMake, initialData, collectionType, bgImage, backgroundColor, titleColor, showStarRatings, isShoppingFeed, isQuickProductCreator, isHero, pageHasH1 } }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [manualChange, setManualChange] = useState(false);
    const [selectedType, setSelectedType] = useState(showType ? typeList[0].type : null);
    const { productFinder, setProductFinder, subtype } = useContext(ProductFinderContext);
    const location = useLocation();
    const [titleData, setTitleData] = useState(null);
    const getDataKey = (sub, coll) => (showType ? coll : `${sub ?? ''}:${coll}`);
    const loadedForRef = useRef(null);
    const inFlightKeyRef = useRef(null);
    const fetchIdRef = useRef(0);
    const dataKeyRef = useRef(getDataKey(subtype, collectionType));
    dataKeyRef.current = getDataKey(subtype, collectionType);
    const fetchTimeoutRef = useRef(null);
    const finderRef = useRef(null);
    const stickyButtonRef = useRef(null);
    const [showStickyButton, setShowStickyButton] = useState(false);
    const [hasChatLauncher, setHasChatLauncher] = useState(false);
    const instance = newFinder ? (showType ? "withType" : "new") : "old";
    const showStickyFinderButton = !isShoppingFeed && !isQuickProductCreator;


    const productFinderWithType = useMemo(() => {
        let copyOfProductFinder = JSON.parse(JSON.stringify(productFinder));

        let keys = Object.keys(copyOfProductFinder);

        if (!showType) {
            keys.forEach(key => {
                delete copyOfProductFinder[key]['type'];
            });
        } else {
            // Find selected type from typeList
            let selectedTypeObj = typeList.find(typeObj => typeObj.value === collectionType);
            const keysToProcess = keys.includes(collectionType) ? keys : [...keys, collectionType];

            keysToProcess.forEach(key => {
                if (!copyOfProductFinder[key]) {
                    copyOfProductFinder[key] = { ...initialState.withType };
                }

                delete copyOfProductFinder[key]['type'];

                copyOfProductFinder[key] = {
                    type: {
                        list: [...typeList], selected: selectedTypeObj?.type ?? null, name: "type",
                    },
                    ...copyOfProductFinder[key],
                };
            });
        }

        return copyOfProductFinder;
    }, [productFinder, showType, collectionType]);


    // If Initial Make already exists, the first dropdown is hidden, so the starting dropdown will be the second one in the row
    const firstDropdownIndex = initialMake || showType ? 1 : 0;

    // onChange if productFinder state changes
    useEffect(() => {
        if (onChange) {
            onChange(productFinderWithType[collectionType]);
        }
    }, [productFinderWithType, collectionType, onChange, subtype]);

    const handleGoToCollection = (selectedModelOption) => {
        if (isQuickProductCreator) return;

        // If there are no options, it means we have not yet reached the last dropdown, return early
        if (productFinderWithType[collectionType]?.option?.list?.length === 0) return;

        NProgress.start();

        setLoading(true);
        const selectedCollection = productFinderWithType[collectionType]?.option?.list?.find((collection) => collection?.option === selectedModelOption) || productFinderWithType[collectionType]?.option?.list[0];
        navigate(addWccParamIfPresent(buildFinderCollectionUrl(selectedCollection)));
    };

    const updateNextDropdownList = async (url, dropdownIndex) => {
        setLoading(true);
        try {
            // nextVehiclePropertyList means that, if we have selected a Make in the Make dropdown, this will return a list of Years for that Make (and so forth for other selections)
            const { data: nextVehiclePropertyList } = await getYMM(`${collectionType}${url}`, newFinder, subtype);

            const dropdownsArr = Object.values(productFinderWithType[collectionType] || initialState[instance]);
            const subsequentReset = dropdownsArr.slice(dropdownIndex + 1).reduce((acc, dropdown, idx) => {
                acc[dropdown.name] = {
                    ...dropdown,
                    selected: null,
                    list: idx === 0 ? nextVehiclePropertyList : [],
                };
                return acc;
            }, {});

            setProductFinder((prevState) => ({
                ...prevState,
                [collectionType]: {
                    ...prevState[collectionType],
                    ...subsequentReset,
                },
            }));

            return setLoading(false);
        } catch (err) {
            sendLog(`[Product Finder] Error: ${err}, URL: ${collectionType}${url}`, NEW_RELIC_LEVELS.FATAL, err);
            setError(true);
        }

        return setLoading(false);
    };

    const handleSelect = async (dropdownName, selectedValue) => {

        if (dropdownName === 'type') {
            // Find type value from typeList, based on .type
            const selectedTypeObj = typeList.find(typeObj => typeObj.type === selectedValue);
            onTypeChange(selectedTypeObj.value);
            return;
        }

        setProductFinder((prevState) => ({
            ...prevState,
            [collectionType]: ({
                ...prevState[collectionType],
                [dropdownName]: { ...prevState[collectionType][dropdownName], selected: selectedValue },
            }),
        }));

        // Which dropdown was just selected
        const dropdownIndex = Object.values(productFinderWithType[collectionType]).findIndex((dropdown) => dropdown.name === dropdownName);

        if (dropdownIndex !== Object.values(productFinderWithType[collectionType]).length - 1) {
            // Since a full URL might be something like this: /vehicle_details?type=car&make=BMW&year=2022,
            // this gets the make/year (if they exist)
            const previousURLs = Object.values(productFinderWithType[collectionType])
                .slice(0, dropdownIndex)
                .map((dropdown) => dropdown.selected !== null && finderQueryParam(dropdown.name, dropdown.selected))
                .filter((url) => url !== false)
                .join("");

            // Creates a new URL to be added (if make => &make=x, if year => &year=x)
            const selectedPropertyURL = finderQueryParam(dropdownName, selectedValue);
            const fullURL = previousURLs + selectedPropertyURL;
            await updateNextDropdownList(fullURL, dropdownIndex);

            setManualChange(true);
        } else {
            handleGoToCollection(selectedValue);
        }
    };

    // Function called on Modal Dropdowns, resets state for the current dropdown, on click we go back from the current dropdown to the previous one
    const handleGoBack = (dropdownName) => {
        const dropdownIndex = Object.values(productFinderWithType[collectionType] || {})
            .findIndex((dropdownEl) => dropdownEl.name === dropdownName);

        const dropdowns = Object.values(productFinderWithType[collectionType] || initialState[instance]);
        const dropdownsToReset = dropdowns.slice(dropdownIndex, dropdowns.length);

        const updatedProductFinder = dropdownsToReset.reduce((acc, dropdown) => {
            acc[dropdown.name] = {
                ...dropdown,
                list: dropdowns[firstDropdownIndex].name === dropdown.name ? dropdown.list : [],
                selected: null,
            };
            return acc;
        }, {});

        setProductFinder((prevState) => ({
            ...prevState,
            [collectionType]: {
                ...prevState[collectionType],
                ...updatedProductFinder,
            },
        }));
    };

    // Called only on the first render
    const handleInitialState = async () => {
        const fetchForKey = getDataKey(subtype, collectionType);
        const fetchId = ++fetchIdRef.current;

        try {
            setLoading(true);

            if (initialData) {

                let path = `${collectionType}`;

                if (initialData.make) { path += finderQueryParam("make", initialData.make); }
                if (initialData.year) { path += finderQueryParam("year", initialData.year); }
                if (initialData.model) { path += finderQueryParam("model", initialData.model); }
                else { path += `&model=`; }
                if (initialData.option) { path += finderQueryParam("option", initialData.option); }

                const data = await getYMMAll(path, newFinder, subtype);
                if (fetchId !== fetchIdRef.current || fetchForKey !== dataKeyRef.current) return;

                let dataObj = {
                    ...initialState[instance],
                }

                if (data.makes) {
                    dataObj.make = { ...initialState[instance].make, selected: initialData.make ?? null, list: data.makes }
                }

                if (data.years) {
                    dataObj.year = { ...initialState[instance].year, selected: initialData.year ?? null, list: data.years }
                }

                if (data.models) {
                    dataObj.model = { ...initialState[instance].model, selected: initialData.model ?? null, list: data.models }
                }

                if (data.options) {
                    dataObj.option = { ...initialState[instance].option, selected: initialData.option ?? null, list: data.options }
                }

                setProductFinder((prevState) => ({
                    ...prevState,
                    [collectionType]: dataObj,
                }));

                loadedForRef.current = fetchForKey;

            } else {
                const { data: makeList, meta } = await getYMM(collectionType, newFinder, subtype);
                if (fetchId !== fetchIdRef.current || fetchForKey !== dataKeyRef.current) return;

                const selectedMake = initialMake
                    ? { selected: initialMake, list: makeList }
                    : { list: makeList };

                setProductFinder((prevState) => ({
                    ...prevState,
                    [collectionType]: ({
                        ...initialState[instance],
                        make: { ...initialState[instance].make, ...selectedMake },
                        option: { ...initialState[instance].option, hidden: !meta?.has_options },
                    }),
                    hiddenOptions: {
                        ...prevState.hiddenOptions,
                        [collectionType]: !meta?.has_options,
                    },
                }));

                loadedForRef.current = fetchForKey;

                if (initialMake) {
                    const yearListURL = finderQueryParam(initialState[instance].make.name, initialMake);
                    updateNextDropdownList(yearListURL, 0);
                    return;
                }
            }
        } catch (err) {
            sendLog(`[Product Finder] Error: ${err}, URL: ${collectionType}`, NEW_RELIC_LEVELS.FATAL, err);
            setError(true);
        } finally {
            if (fetchId === fetchIdRef.current) {
                setLoading(false);
            }
        }
    };

    // Called when the component is first mounted, since we initially need a List of Makes and optionally a selected Make coming from Sanity
    const setStateOnInitialMount = async () => {
        const { make } = productFinderWithType[collectionType] || {};

        if (isShoppingFeed) {
            await handleInitialState();
            return;
        }

        const dataIsCurrent = loadedForRef.current === getDataKey(subtype, collectionType);

        // If our make.selected is already the same as the initialMake, return early
        // This handles the case where we navigate away and return to the first landing page
        if (initialMake && make?.list && make?.selected === initialMake && dataIsCurrent) return;

        // If there is no initialMake, but there is a list for this subtype/collectionType, there is no need to re-update the state
        if (!initialMake && make?.list && dataIsCurrent) return;

        await handleInitialState();
    };

    useEffect(() => {
        if (typeof window !== "undefined" && window.location.search) {
            const urlParams = new URLSearchParams(window.location.search);
            const make = urlParams.get("make");
            const model = urlParams.get("model");

            if (make) {
                initialMake = make;
            }

            if (make && model) {
                initialData = {};
                initialData.make = make;
                initialData.model = model;
            }

            setTitleData({
                make,
                model,
            });
        }
    }, []);

    useEffect(() => {
        if (!collectionType) return undefined;

        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
        }

        fetchTimeoutRef.current = setTimeout(() => {
            const dataKey = getDataKey(subtype, collectionType);

            if (loadedForRef.current === dataKey) {
                setStateOnInitialMount();
                return;
            }

            if (inFlightKeyRef.current === dataKey) return;

            inFlightKeyRef.current = dataKey;

            if (loadedForRef.current !== null && loadedForRef.current !== dataKey) {
                setProductFinder((prev) => ({
                    ...prev,
                    [collectionType]: initialState[instance],
                }));
            }

            handleInitialState().finally(() => {
                if (inFlightKeyRef.current === dataKey) {
                    inFlightKeyRef.current = null;
                }
            });
        }, 100);

        return () => {
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
    }, [subtype, collectionType]);

    function getTitle() {
        if (titleData?.make && titleData?.model) {
            return `Find your ${titleData.make} ${titleData.model} Cover`;
        }

        return title;
    }

    /*
      Should navigate to the selected collection page if there is only one option in the option.list.
      Using useEffectWithoutFirstRender, since if there is only one option, which navigates us to that Option Page
      if we navigate back, it shouldn't navigate us again to the Option Page (in the process, creating an infinite loop)
    */
    useEffectWithoutFirstRender(() => {
        if (isQuickProductCreator) return;

        if (productFinderWithType[collectionType]?.option?.list?.length === 1 && manualChange) {
            const collectionModel = productFinderWithType[collectionType]?.option?.list[0];
            const fullUrl = buildFinderCollectionUrl(collectionModel);

            // setProductFinder((prevState) => ({
            //     ...prevState,
            //     [collectionType]: {
            //         ...prevState[collectionType],
            //         option: { ...prevState[collectionType].option, selected: collectionModel.option },
            //     },
            // }));

            NProgress.start();

            navigate(addWccParamIfPresent(fullUrl));
        } else NProgress.done();
    }, [productFinderWithType[collectionType]?.option?.list, manualChange]);

    useEffect(() => {
        if (!showStickyFinderButton) return undefined;

        const finderEl = finderRef.current;
        if (!finderEl || typeof IntersectionObserver === "undefined") return undefined;

        const observer = new IntersectionObserver(
            ([entry]) => {
                const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
                setShowStickyButton(scrolledPast);
            },
            { threshold: 0 },
        );

        observer.observe(finderEl);
        return () => observer.disconnect();
    }, [showStickyFinderButton, collectionType]);

    useEffect(() => {
        if (!showStickyFinderButton) return undefined;

        const applyChatOffset = () => {
            const stickyEl = stickyButtonRef.current;
            const offset = getStickyChatOffset();
            setHasChatLauncher(offset > 0);
            if (!stickyEl) return;
            if (offset > 0) {
                stickyEl.style.setProperty("--product-finder-sticky-chat-offset", `${offset}px`);
            } else {
                stickyEl.style.removeProperty("--product-finder-sticky-chat-offset");
            }
        };

        applyChatOffset();

        const observer = new MutationObserver(applyChatOffset);
        observer.observe(document.body, { childList: true, subtree: true });
        const chatContainer = document.getElementById("chat-widget-container");
        if (chatContainer) {
            observer.observe(chatContainer, { attributes: true, subtree: true, attributeFilter: ["style", "class", "hidden"] });
        }

        window.addEventListener("resize", applyChatOffset);

        const chatWidget = window.LiveChatWidget;
        if (chatWidget?.on) {
            chatWidget.on("ready", applyChatOffset);
            chatWidget.on("visibility_changed", applyChatOffset);
        }

        const intervalId = window.setInterval(applyChatOffset, 1000);
        const timeoutId = window.setTimeout(() => window.clearInterval(intervalId), 20000);

        return () => {
            observer.disconnect();
            window.removeEventListener("resize", applyChatOffset);
            window.clearInterval(intervalId);
            window.clearTimeout(timeoutId);
            if (chatWidget?.off) {
                chatWidget.off("ready", applyChatOffset);
                chatWidget.off("visibility_changed", applyChatOffset);
            }
        };
    }, [showStickyFinderButton]);

    const scrollToFinder = () => {
        finderRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    if (!collectionType || error) return <Error bgImage={bgImage} pathname={location.pathname} backgroundColor={backgroundColor} />;

    const TitleTag = pageHasH1 === false ? "h1" : "h2";

    return (
        <>
            <Container bgImage={bgImage} pathname={location.pathname} backgroundColor={backgroundColor} isShoppingFeed={isShoppingFeed} containerRef={finderRef}>
                {showStarRatings ? (
                    <div className="flex justify-center w-full">
                        <div className="inline-flex flex-col items-start max-w-full">
                            <div className="flex items-center gap-2 mb-3">
                                <Stars rating={5} className="justify-start items-center !gap-1" color={titleColor?.hex || "#ffffff"} />
                                <span className="text-sm md:text-base font-medium leading-none" style={{ color: "#91CDFD" }}>
                                    130K+ 5 Star Reviews
                                </span>
                            </div>
                            <FinderTitle as={TitleTag} titleColor={titleColor}>
                                {getTitle()}
                            </FinderTitle>
                        </div>
                    </div>
                ) : (
                    <FinderTitle as={TitleTag} titleColor={titleColor}>
                        {getTitle()}
                    </FinderTitle>
                )}
                {subtitle &&
                    <h2 className="text-white text-center mb-5 uppercase font-semibold">
                        {subtitle}
                    </h2>
                }
                {loading && <Loading className={`fixed lg:absolute ${isShoppingFeed ? 'rounded-[21px]' : ''}`} />}

                <DesktopDropdowns
                    dropdownsArr={Object.values(productFinderWithType[collectionType] || initialState[instance])}
                    initialMake={initialMake}
                    onSelect={handleSelect}
                    hideOptionDropdown={productFinderWithType?.hiddenOptions ? productFinderWithType.hiddenOptions[collectionType] : false}
                    onGoToCollection={handleGoToCollection}
                    className="lg:flex hidden ymm-dropdown-container"
                    isShoppingFeed={isShoppingFeed}
                    isQuickProductCreator={isQuickProductCreator}
                    isHero={isHero}
                />
                <MobileDropdowns
                    dropdownsArr={Object.values(productFinderWithType[collectionType] || initialState[instance])}
                    initialMake={initialMake}
                    onSelect={handleSelect}
                    hideOptionDropdown={productFinderWithType?.hiddenOptions ? productFinderWithType.hiddenOptions[collectionType] : false}
                    onGoToCollection={handleGoToCollection}
                    className="lg:hidden flex"
                    onGoBack={handleGoBack}
                    firstDropdownIndex={firstDropdownIndex}
                    isShoppingFeed={isShoppingFeed}
                    isQuickProductCreator={isQuickProductCreator}
                    isHero={isHero}
                />
            </Container>
            {/* {showStickyFinderButton && (
                <div
                    ref={stickyButtonRef}
                    className={`product-finder-sticky${showStickyButton ? " product-finder-sticky--visible" : ""}${hasChatLauncher ? " product-finder-sticky--with-chat" : ""}`}
                >
                    <button
                        type="button"
                        className="button button--full button--primary-sates button--no-wrap product-finder-sticky__button"
                        onClick={scrollToFinder}
                        tabIndex={showStickyButton ? 0 : -1}
                        aria-hidden={!showStickyButton}
                    >
                        {getStickyButtonLabel(collectionType, subtype)}
                    </button>
                </div>
            )} */}
        </>
    );
};

export default ProductFinder;
